import React, { useEffect, useState } from 'react';
import { Building2 } from 'lucide-react';
import { marketingAPI } from '@/lib/api';

/**
 * Phase 27.38.1 — Shared agency logo tile.
 *
 * Fetches the uploaded logo bytes via the auth-safe blob endpoint and
 * renders them inside a rounded tile. When no logo is on file we fall
 * back to a Building2 icon on a navy gradient so lists never show a
 * broken image.
 *
 * Deliberately kept small — no upload UI here. Uploads live in the
 * agency header widget (`AgencyLogoWidget`).
 *
 * Props:
 *   agency    { agency_id, agency_name, logo_path } — required
 *   size      Tailwind size class combo (default: "w-8 h-8")
 *   rounded   "lg" | "full" (default: "lg")
 *   iconSize  Icon px size for the fallback (default: 16)
 *   testId    data-testid override (default: "agency-logo")
 */
export function AgencyLogo({
  agency,
  size = 'w-8 h-8',
  rounded = 'lg',
  iconSize = 16,
  testId = 'agency-logo',
  className = '',
}) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    if (agency?.logo_path) {
      marketingAPI
        .attachmentBlob(agency.logo_path)
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          revoke = url;
          setSrc(url);
        })
        .catch(() => setSrc(null));
    } else {
      setSrc(null);
    }
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [agency?.logo_path]);

  const roundedCls = rounded === 'full' ? 'rounded-full' : 'rounded-lg';
  const wrapperStyle = src
    ? { background: '#fff', border: '1px solid var(--kr-border)' }
    : {
        background: 'linear-gradient(135deg, var(--kr-navy-700), var(--kr-navy-900))',
        color: '#fff',
      };

  return (
    <div
      className={`${size} ${roundedCls} flex items-center justify-center overflow-hidden flex-shrink-0 ${className}`}
      style={wrapperStyle}
      data-testid={testId}
      title={agency?.agency_name || ''}
    >
      {src ? (
        <img
          src={src}
          alt={`${agency?.agency_name || 'Agency'} logo`}
          className="max-w-full max-h-full object-contain"
        />
      ) : (
        <Building2 size={iconSize} />
      )}
    </div>
  );
}

export default AgencyLogo;
